import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Table, Tag } from "antd";
import styled from "styled-components";
import TableFilter from "./TableFilter";
import _ from "lodash";
import { useRecoilState } from "recoil";
import { vendorSearchAtom } from "Store/crmAtom";
import { useLocation, Link } from 'react-router-dom'
import { THEME, DriverBookingStatusColors, DriverBookingStatusLabels } from "Util/constants";
import padWithLeadingZeroes from "leading-zeroes";
import { LogisticsJobsContext } from "..";
import { isDriverActive } from "Util/driverSchedule";



const Container = styled.div`
	.ant-table-thead {
		.ant-table-cell {
			color: #999;
			font-weight: bold;
		}
	}

    .ant-table-tbody > tr:hover > td {
		background-color: "none";
	}
	.ant-table-tbody > tr.selected > td {
		background-color: ${THEME.colors.primary};
	}

`;

const renderColumns =  () => {
    const columns = [
    {
        title: 'Driver Name',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name?.localeCompare(b.name),
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: '10%'
    },
    {
        title: 'Driver License No.',
        dataIndex: 'license',
        key: 'license',
        sorter: (a, b) => a.license?.localeCompare(b.license),
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: '12%'
    },
    {
        title: 'Driver Type',
        dataIndex: 'type',
        key: 'type',
        sorter: (a, b) => a.type?.localeCompare(b.type),
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (text) => <>Reefer Truck</>,
        width: '10%'
    },
    {
        title: 'COV',
        dataIndex: 'cov',
        key: 'cov',
        sorter: (a, b) => a.cov?.localeCompare(b.cov),
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (text) => <>MCWG, LMV, TRANS</>,
        width: '12%'
    },
    {
        title: 'Mobile No.',
        dataIndex: 'phone',
        key: 'phone',
        sorter: (a, b) => a.phone?.localeCompare(b.phone),
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: '12%'
    },
    {
        title: 'Address',
        dataIndex: 'address',
        key: 'address',
        sorter: (a, b) => a.address?.localeCompare(b.address),
        sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
        title: 'Aadhaar ID',
        dataIndex: 'aadhaar_id',
        key: 'aadhaar_id',
        width: '10%',
        sorter: (a, b) => a.aadhaar_id?.localeCompare(b.aadhaar_id),
        sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
        title: 'Availability',
        key: 'availability',
        width: '10%',
        render: (_text, record) => {
            const active = isDriverActive(record.active_schedule)
            return <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Off Duty'}</Tag>
        },
        sorter: (a, b) => Number(isDriverActive(b.active_schedule)) - Number(isDriverActive(a.active_schedule)),
    },
    {
        title: 'Booking Status',
        dataIndex: 'booking_status',
        key: 'booking_status',
        width: '10%',
        render: (v) => {
            const status = v ?? 'available'
            return <Tag color={DriverBookingStatusColors[status]}>{DriverBookingStatusLabels[status] ?? status}</Tag>
        },
        sorter: (a, b) => (a.booking_status ?? '').localeCompare(b.booking_status ?? ''),
    }
    ];

    return columns
}

function DataTable({
    pageSize = 50,
    height = 250,
    data = [],
    loading = false
}) {
    const { selectedRecord, setSelectedRecord } = useContext(LogisticsJobsContext)
    const [search, setSearch ] = useRecoilState(vendorSearchAtom)
    const location = useLocation();

    const searchable = data.map((obj, i) => {
        let result = [];
        let concatenatedValues = "";
        renderColumns().forEach(column => {
          if (obj[column.key] && obj[column.key] !== undefined) concatenatedValues += obj[column.dataIndex] + " ";
        });
        result.push(concatenatedValues.trim());
    
        return {
          ...obj,
          searchText:  result.toString()
        }
      })
    
    const filterData = (data) => {
        if(!search){
            return data
        }
        return _.filter(searchable, function (d) { 
            let query = search.toLowerCase();
            return _.includes(d.searchText.toLowerCase(), query)
        }); 
    };
    
    const handleRowClick = (row) => {
        setSelectedRecord(row);
    }
    
    useEffect(() => {
        setSelectedRecord({})
    }, [location.pathname, setSelectedRecord])
    
    return (
        <Container>
            <TableFilter data={filterData(data)} column={renderColumns()}/>
            <Table
                loading={loading}
                columns={renderColumns()}
                dataSource={filterData(data)}
                onRow={(rec, idx) => ({
                    onClick: (e) => {
                      handleRowClick(rec)
                    }       
                })}
                pagination={{ defaultPageSize: pageSize }}
                scroll={{ y: height, x: 1500 }}
                rowKey={rec => parseInt(rec.id, 10)}
                components={{
                    body: {
                        row: (props) => {
                            const row = _.find(filterData(data), { id: props['data-row-key'] })
                            let selected = false;
                            if (!_.isEmpty(selectedRecord) && !_.isEmpty(row)) {
                                selected = row.id === selectedRecord.id;
                            }
                            const tableRow = (
                                <tr 
                                    {...props}
                                    style={{ cursor: 'pointer' }} 
                                    onClick={props.onClick}
                                    className={`ant-table-row ant-table-row-level-0 yeah ${selected ? "selected" : ""}`}
                                >
                                    {props.children}
                                </tr>
                            )

                            return tableRow
                        }
                    }
                }}
            />
        </Container>
    )

}

DataTable.propTypes = {
    pageSize: PropTypes.oneOf([10, 20, 50, 100]),
    height: PropTypes.number,
    data: PropTypes.arrayOf(PropTypes.object),
    loading: PropTypes.bool,
};

export default DataTable;
